import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  Close as CloseIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

/**
 * Props for the DocumentViewer component
 * @interface DocumentViewerProps
 * @property {string} fileUrl - The URL of the document to be displayed
 * @property {string} fileName - The name of the file to be displayed in the header
 * @property {() => void} onClose - Callback function to handle closing the viewer
 */
interface DocumentViewerProps {
  fileUrl: string;
  fileName: string;
  onClose: () => void;
}

/**
 * A React component for displaying document files (PDF, DOC, DOCX) using Google Docs Viewer.
 * Provides a modal dialog with loading states and error handling.
 * 
 * @component
 * @param {DocumentViewerProps} props - The component props
 * @returns {JSX.Element} A dialog containing the document viewer
 * 
 * @example
 * <DocumentViewer
 *   fileUrl="https://example.com/document.pdf"
 *   fileName="Sample Document.pdf"
 *   onClose={() => console.log('Viewer closed')}
 * />
 */
const DocumentViewer: React.FC<DocumentViewerProps> = ({
  fileUrl,
  fileName,
  onClose,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeKey, setIframeKey] = useState<number>(0);

  // Determine file type
  const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
  const isPdf = fileExtension === 'pdf';
  const isOfficeFile = ['xlsx', 'xls', 'docx', 'doc'].includes(fileExtension);

  // Initialize PDF viewer plugin
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  /**
   * Handles the load event of the iframe
   * @function handleIframeLoad
   * @returns {void}
   */
  const handleIframeLoad = () => {
    setLoading(false);
  };

  /**
   * Handles errors that occur when loading the iframe
   * @function handleIframeError
   * @returns {void}
   */
  const handleIframeError = () => {
    setLoading(false);
    setError(
      "Failed to load document preview. The file may be corrupted or in an unsupported format."
    );
  };

  /**
   * Handles downloading the document file
   * @function handleDownload
   * @returns {Promise<void>}
   */
  const handleDownload = async () => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  /**
   * Generates a Microsoft Office Online viewer URL for the document
   * This is more reliable for authenticated URLs than Google Docs Viewer
   * @constant {string}
   */
  const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;

  // Re-render iframe when URL changes to ensure proper loading
  useEffect(() => {
    setIframeKey((prevKey) => prevKey + 1);
    setLoading(true);
    setError(null);
  }, [fileUrl]);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          style: {
            height: "90vh",
            maxHeight: "90vh",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isPdf ? <PdfIcon color="primary" /> : <DescriptionIcon color="primary" />}
          <Typography variant="h6" noWrap sx={{ maxWidth: "600px" }}>
            {fileName}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!isPdf && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{
                textTransform: "none",
              }}
            >
              Download
            </Button>
          )}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, position: "relative", height: "calc(90vh - 64px)" }}>
        {error ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="error">{error}</Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              You can still download the document to view it with a compatible
              application.
            </Typography>
            <Button
              variant="contained"
              onClick={handleDownload}
              sx={{ mt: 2 }}
            >
              Download Document
            </Button>
          </Box>
        ) : (
          <>
            {isPdf ? (
              <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
                <Viewer
                  fileUrl={fileUrl}
                  plugins={[defaultLayoutPluginInstance]}
                />
              </Worker>
            ) : isOfficeFile ? (
              <>
                {loading && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <iframe
                  key={iframeKey}
                  src={officeViewerUrl}
                  title={fileName}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    display: loading ? "none" : "block",
                  }}
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                />
              </>
            ) : (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2">
                  Unsupported file format. Please download the file to view it.
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleDownload}
                  sx={{ mt: 2 }}
                >
                  Download File
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewer;
